import { Subscription } from 'rxjs';
import { ModalRouteService } from './../../../services/modal-route.service';
import { Router } from '@angular/router';
import { SapiService } from './../../../services/sapi.service';
import { QueryParameter } from 'app/interfaces/queryString';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, forwardRef, SimpleChanges, EventEmitter, Output, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LineupScheduleCycle } from './../../../interfaces/lineup';

@Component({
	selector: 'bb-edit-cycle',
	templateUrl: './bb-edit-cycle.component.html',
	styleUrls: ['./bb-edit-cycle.component.scss'],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbEditCycleComponent),
		multi: true
	}]

})

export class BbEditCycleComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	@Input() min: number;
	@Input() max: number;
	@Input() minDuration: number = 10 * 60;
	@Output() onDelete = new EventEmitter();

	previousFillerItemId = null;
	cycle: LineupScheduleCycle;
	cycleItems: any[] = [];
	loadingItems = false;
	newCliplists: any = {};
	itemFilterSet = [{
		filters: [{
			type: 'search',
			field: 'type',
			operator: 'is',
			value: ['MediaClipList']
		},
		{
			type: 'search',
			field: 'mediatype',
			operator: 'is',
			value: ['video']
		}]
	},
	{
		filters: [{
			type: 'search',
			field: 'sourcetype',
			operator: 'isNot',
			value: ['live']
		}]
	}];
	fillerFilterSet = [{
		filters: [{
			type: 'search',
			field: 'type',
			operator: 'is',
			value: ['MediaClipList']
		},
		{
			type: 'search',
			field: 'mediatype',
			operator: 'is',
			value: ['video']
		}]
	}];
	subscriptions = new Subscription();

	constructor(
		public modalRouteService: ModalRouteService,
		private sapiService: SapiService,
		private router: Router
	) { }

	ngOnInit() {
		this.subscriptions.add(this.modalRouteService.onCloseSubject.subscribe(() => {
			setTimeout(() => {
				// allow some time for solr to update..
				this.getCycleItems(true);
			}, 2000);
		}));
	}

	ngOnChanges(changes: SimpleChanges) { }

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	trackBy(index: number, item: any) {
		return item && item.id || index;
	}

	onItemSelected(item) {
		this.cycleItems.push(item);
		this.updateCycleItems();
	}

	onFillerItemSelected(newFillerItem) {
		// search for filler item in cycle.items which hasn't been updated yet. If it exists, remove the previous filler item first
		const fillerItemIndex = this.cycle.items.findIndex((item) => this.previousFillerItemId === item.entityId);
		if (fillerItemIndex > -1) {
			this.cycleItems.pop();
		}
		this.cycleItems.push(newFillerItem);
		this.updateCycleItems();
	}

	onFillerItemChange(value) {
		if (value === null) {
			this.cycleItems.pop();
			this.previousFillerItemId = null;
		}
		// will be updated by "onFillerItemSelected"
	}

	onRemoveItemClicked(i: number) {
		if (this.cycleItems[i].id === this.cycle.fillerItemId) {
			this.cycle.fillerItemId = null;
		}
		this.cycleItems.splice(i, 1);
		this.updateCycleItems();
	}

	goToEntity(item) {
		if (item && item.id) {
			this.router.navigate(item.type === 'MediaClip' ? ['/library/mediaclip', item.id, 'metadata'] : ['/library/playlist', item.id, 'general']);
		}
	}

	editEntity(entity) {
		const type = entity.type === 'MediaClipList' ? 'playlist' : entity.type;
		this.modalRouteService.open(type, entity.id);
	}

	addNewPlaylist(type) {
		this.modalRouteService.open('playlist', 'new_' + type);
		const sapiChangeSubscription = this.sapiService.getSapiChanges().subscribe((sapiChange) => {
			const cliplist = sapiChange.response;
			if (cliplist && sapiChange.entityName === 'cliplist' && sapiChange.request === 'create') {
				const newItemInCycle = this.cycleItems.find((item) => item.id === sapiChange.response.id);
				if (cliplist) {
					// cache new cliplist since it takes a while to index it in solr
					this.newCliplists[cliplist.id] = cliplist;
				}
				if (!newItemInCycle) {
					this.cycleItems.push(sapiChange.response);
					this.updateCycleItems();
				}
				sapiChangeSubscription.unsubscribe();
			}
		});
	}

	itemDragged(event: CdkDragDrop<string[]>) {
		if (event.currentIndex === event.previousIndex) {
			return;
		}
		moveItemInArray(this.cycleItems, event.previousIndex, event.currentIndex);
		this.updateCycleItems();
	}

	updateCycleItems() {
		const fillerItemIndex = this.cycleItems.findIndex((cycleItem) => this.cycle.fillerItemId === cycleItem.id);
		if (fillerItemIndex > -1 && fillerItemIndex !== this.cycleItems.length - 1) {
			// keep filler item last
			moveItemInArray(this.cycleItems, fillerItemIndex, this.cycleItems.length - 1);
		}
		this.cycle.items = this.cycleItems.map((item) => ({
			entityType: item.type,
			entityId: item.id
		}));
		this.previousFillerItemId = this.cycle.fillerItemId;
		this.propagate();
	}

	getCycleItems(withoutLoad = false) {
		if (this.cycle && this.cycle.items && this.cycle.items.length) {
			let q = '';
			this.cycle.items.forEach((item, i) => {
				q += `(id:"${item.entityId}" AND type:"${item.entityType}") `;
				if (i < this.cycle.items.length - 1) {
					q += ' OR ';
				}
			});
			const params: QueryParameter[] = [{
				name: 'q',
				values: [q]
			}];
			if (!withoutLoad) {
				this.loadingItems = true;
			}
			this.sapiService.query('search', params).subscribe((res) => {
				this.loadingItems = false;
				if (!res || !res.items) {
					// error?
					return;
				}
				const newItems = [];
				this.cycle.items.forEach(ci => {
					let searchItem = res.items.find(resItem => resItem.id === ci.entityId);
					if (!searchItem && this.newCliplists[ci.entityId]) {
						// clipstlists take very long to be updated in solr..
						searchItem = this.newCliplists[ci.entityId];
					} else if (!searchItem) {
						// just keep id/type instead
						searchItem = {
							id: ci.entityId,
							type: ci.entityType
						};
					}
					newItems.push(searchItem);
				});
				this.cycleItems = newItems;
			});
		}
	}

	propagate() {
		this.propagateChange(this.cycle);
	}

	update() {
		this.previousFillerItemId = this.cycle && this.cycle.fillerItemId;
		this.getCycleItems();
	}

	onDeleteClick() {
		this.onDelete.emit();
	}

	// ControlValueAccessor functions:

	writeValue(value: any) {
		this.cycle = value;
		this.update();
	}

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}

	propagateChange(_: any) { }
}
